/**
 * Host Room — auto-joins signaling and auto-starts the call when paired.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import useRoomSession from "../webrtc/useRoomSession";
import { buildRoomId, fetchInviteTicket } from "../api/signalingApi";
import { MediaToggles } from "../ui/MediaToggles";

function statusBannerStyle(status) {
  const base = {
    marginBottom: 14,
    padding: "12px 14px",
    borderRadius: 10,
    fontSize: 14,
    lineHeight: 1.45,
  };
  switch (status) {
    case "connecting":
      return { ...base, background: "#fffbeb", border: "1px solid #fcd34d", color: "#92400e" };
    case "waiting":
      return { ...base, background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" };
    case "in_call":
      return { ...base, background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" };
    case "left":
      return { ...base, background: "#f3f4f6", border: "1px solid #d1d5db", color: "#374151" };
    case "error":
      return { ...base, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" };
    default:
      return { ...base, background: "#f9fafb", border: "1px solid #e5e7eb", color: "#4b5563" };
  }
}

function statusLabel(status) {
  switch (status) {
    case "connecting":
      return "Connecting";
    case "waiting":
      return "In room — waiting";
    case "in_call":
      return "In call";
    case "left":
      return "Left room";
    case "error":
      return "Error";
    default:
      return "Idle";
  }
}

export default function Room() {
  const [companyId, setCompanyId] = useState("");
  const [roomSlug, setRoomSlug] = useState("");
  const [peerId, setPeerId] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("stage");
  const [authError, setAuthError] = useState("");
  const [authLoaded, setAuthLoaded] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const logBoxRef = useRef(null);

  const roomId = useMemo(
    () => buildRoomId(companyId, roomSlug),
    [companyId, roomSlug]
  );

  useEffect(() => {
    document.title = "Room";
  }, []);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload || {};
        if (!mounted) return;
        const cid = (payload["custom:companyId"] || "").trim();
        const sub = payload.sub || "";
        const mail = payload.email || "";
        setCompanyId(cid);
        setPeerId(sub);
        setEmail(mail);
        if (!cid) {
          setAuthError(
            "Missing custom:companyId on this account. Contact your administrator."
          );
        }
      } catch (err) {
        if (mounted) setAuthError("Could not load Cognito session.");
      } finally {
        if (mounted) setAuthLoaded(true);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const getIdToken = useCallback(async () => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || "";
  }, []);

  const session = useRoomSession({
    roomId,
    peerId,
    displayName: email || peerId,
    role,
    getIdToken,
    localVideoRef,
    remoteVideoRef,
    autoConnect: Boolean(authLoaded && roomId && peerId && !authError),
  });

  useEffect(() => {
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [session.logLines]);

  const mintInvite = useCallback(async () => {
    setInviteBusy(true);
    setInviteError("");
    setInviteUrl("");
    try {
      const token = await getIdToken();
      const minted = await fetchInviteTicket({ idToken: token, roomId });
      const url = `${window.location.origin}/join?ticket=${encodeURIComponent(minted.ticket)}`;
      setInviteUrl(url);
    } catch (e) {
      setInviteError(e?.message || String(e));
    } finally {
      setInviteBusy(false);
    }
  }, [getIdToken, roomId]);

  if (!authLoaded) {
    return <div style={{ padding: 16 }}>Loading account…</div>;
  }

  if (authError) {
    return (
      <div style={{ padding: 16, color: "#991b1b" }}>
        <h2 style={{ marginTop: 0 }}>Room unavailable</h2>
        <p>{authError}</p>
      </div>
    );
  }

  const statusColor = {
    new: "#6b7280",
    connecting: "#d97706",
    connected: "#059669",
    disconnected: "#d97706",
    failed: "#dc2626",
    closed: "#6b7280",
  }[session.connectionState] || "#6b7280";

  function formatRateLine(kind, rates) {
    const send = rates?.send != null ? rates.send : "-";
    const recv = rates?.recv != null ? rates.recv : "-";
    return `${kind.toUpperCase()} ➜  Send: ${send} kbps | Recv: ${recv} kbps`;
  }

  const locked = session.inRoom || session.connecting;

  return (
    <div style={styles.page}>
      <h1 style={{ marginTop: 0 }}>Room</h1>
      <p style={styles.muted}>
        Entering this page joins signaling automatically. The call starts when
        another participant is present. Leave exits the room and disconnects.
      </p>

      <div style={statusBannerStyle(session.sessionStatus)} role="status">
        <strong>{statusLabel(session.sessionStatus)}</strong>
        {session.statusDetail ? (
          <div style={{ marginTop: 4 }}>{session.statusDetail}</div>
        ) : null}
        {session.sessionStatus === "left" ? (
          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={session.rejoin} style={styles.btn}>
              Rejoin room
            </button>
            <Link to="/" style={{ ...styles.btn, textDecoration: "none", display: "inline-block" }}>
              Back to dashboard
            </Link>
          </div>
        ) : null}
      </div>

      {session.roomError ? (
        <div style={styles.errorBanner} role="alert">
          <div>
            <strong>Room busy</strong>
            <div style={{ marginTop: 4 }}>{session.roomError}</div>
          </div>
          <button type="button" onClick={session.clearRoomError} style={styles.btn}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div style={styles.layout}>
        <div style={styles.left}>
          <fieldset style={styles.fieldset}>
            <legend>Identity &amp; Room</legend>
            <div style={styles.row}>
              <label style={styles.label}>
                Role:
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={locked}
                  style={styles.select}
                >
                  <option value="stage">Stage</option>
                  <option value="client">Viewer</option>
                </select>
              </label>
              <label style={styles.label}>
                Room slug (optional):
                <input
                  value={roomSlug}
                  onChange={(e) => setRoomSlug(e.target.value)}
                  disabled={locked}
                  placeholder="e.g. lobby"
                  style={{ ...styles.select, width: 120 }}
                />
              </label>
              <label style={styles.label}>
                Audio bitrate:
                <select
                  value={session.audioKbps}
                  onChange={(e) => session.setAudioBitrate(e.target.value)}
                  disabled={!session.canEditMediaPolicy}
                  style={styles.select}
                >
                  {session.audioBitrateOptions.map((kbps) => (
                    <option key={kbps} value={kbps}>
                      {kbps}
                    </option>
                  ))}
                </select>{" "}
                kbps
              </label>
              <label style={styles.label}>
                Video bitrate:
                <select
                  value={session.videoKbps}
                  onChange={(e) => session.setVideoBitrate(e.target.value)}
                  disabled={!session.canEditMediaPolicy}
                  style={styles.select}
                >
                  {session.videoBitrateOptions.map((kbps) => (
                    <option key={kbps} value={kbps}>
                      {kbps}
                    </option>
                  ))}
                </select>{" "}
                kbps
              </label>
              <div style={styles.kv}>
                <strong>Policy:</strong>{" "}
                {session.mediaPolicySource === "org"
                  ? "org default"
                  : session.mediaPolicySource === "room"
                    ? "room override"
                    : "local"}
                {!session.canEditMediaPolicy ? " (read-only)" : ""}
              </div>
              <div style={styles.kv}>
                <strong>Company:</strong> {companyId}
              </div>
              <div style={styles.kv}>
                <strong>Room ID:</strong>{" "}
                <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                  {roomId}
                </span>
              </div>
              <div style={styles.kv}>
                <strong>Peer ID:</strong>{" "}
                <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                  {peerId}
                </span>
              </div>
              <div style={styles.kv}>
                <strong>Display:</strong> {email || "—"}
              </div>
              <div style={styles.kv}>
                <strong>PC:</strong>{" "}
                <span style={{ color: statusColor, fontWeight: 600 }}>
                  {session.connectionState}
                </span>
                {session.wsOpen ? (
                  <span style={{ marginLeft: 8, color: "#059669" }}>WS open</span>
                ) : (
                  <span style={{ marginLeft: 8, color: "#6b7280" }}>WS closed</span>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset style={styles.fieldset}>
            <legend>Guest invite</legend>
            <div style={styles.row}>
              <button
                type="button"
                onClick={mintInvite}
                disabled={!roomId || inviteBusy}
                style={styles.btn}
              >
                {inviteBusy ? "Minting…" : "Create invite link"}
              </button>
              {inviteUrl ? (
                <button
                  type="button"
                  style={styles.btn}
                  onClick={() => navigator.clipboard?.writeText(inviteUrl)}
                >
                  Copy link
                </button>
              ) : null}
            </div>
            {inviteError ? (
              <p style={{ color: "#991b1b", fontSize: 13, marginBottom: 0 }}>
                {inviteError}
              </p>
            ) : null}
            {inviteUrl ? (
              <input
                readOnly
                value={inviteUrl}
                style={{ ...styles.inviteInput, marginTop: 8 }}
                onFocus={(e) => e.target.select()}
              />
            ) : (
              <p style={{ ...styles.muted, marginBottom: 0, marginTop: 8 }}>
                Guests open the link with no Cognito login.
              </p>
            )}
          </fieldset>

          <fieldset style={styles.fieldset}>
            <legend>Controls</legend>
            <div style={styles.row}>
              {session.sessionStatus === "left" || session.sessionStatus === "error" ? (
                <button type="button" onClick={session.rejoin} style={styles.btn}>
                  Rejoin room
                </button>
              ) : null}
              <button
                type="button"
                onClick={session.hangup}
                disabled={!session.canHangup}
                aria-disabled={!session.canHangup}
                style={{
                  ...styles.btn,
                  ...(session.canHangup ? styles.btnDanger : styles.btnLeft),
                }}
              >
                {session.sessionStatus === "left" ? "Left" : "Leave"}
              </button>
              <MediaToggles
                cameraOn={session.cameraOn}
                micOn={session.micOn}
                canMedia={session.canMedia}
                onToggleCamera={session.toggleCamera}
                onToggleMic={session.toggleMic}
              />
            </div>
            <p style={{ ...styles.muted, margin: "8px 0 0" }}>
              Camera and Mic are toggles — green means on; muted mic shows red Off.
            </p>
          </fieldset>

          <fieldset style={styles.fieldset}>
            <legend>Roster</legend>
            <textarea readOnly value={session.rosterText} style={styles.roster} />
            {session.remotePeerId ? (
              <div style={{ ...styles.muted, marginTop: 6 }}>
                Paired with: {session.remotePeerId}
              </div>
            ) : null}
          </fieldset>

          <fieldset style={styles.fieldset}>
            <legend>Video</legend>
            <div style={styles.videoGrid}>
              <div>
                <div>
                  <strong>Local (self-view)</strong>
                </div>
                <video
                  ref={localVideoRef}
                  playsInline
                  autoPlay
                  muted
                  style={styles.video}
                />
              </div>
              <div>
                <div>
                  <strong>Remote</strong>
                </div>
                <video
                  ref={remoteVideoRef}
                  playsInline
                  autoPlay
                  style={styles.video}
                />
              </div>
            </div>
          </fieldset>

          <fieldset style={styles.fieldset}>
            <legend>Stats</legend>
            <div style={styles.stats}>
              {formatRateLine("audio", session.mediaRates.audio)}
              {"\n"}
              {formatRateLine("video", session.mediaRates.video)}
            </div>
          </fieldset>
        </div>

        <div style={styles.right}>
          <fieldset style={styles.fieldset}>
            <legend>Logs</legend>
            <div ref={logBoxRef} style={styles.log}>
              {session.logLines.join("\n")}
            </div>
          </fieldset>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { maxWidth: 1200 },
  muted: { color: "#6b7280", fontSize: 14, lineHeight: 1.5 },
  errorBanner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 14,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: 16,
    alignItems: "start",
  },
  left: {},
  right: { position: "sticky", top: 16 },
  fieldset: {
    marginBottom: 14,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 12,
    background: "#fff",
  },
  row: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  label: { fontWeight: 600, fontSize: 14 },
  select: { marginLeft: 8, padding: "6px 8px" },
  kv: { fontSize: 14 },
  inviteInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    fontFamily: "ui-monospace, monospace",
    fontSize: 12,
  },
  btn: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#e5e7eb",
    cursor: "pointer",
    fontSize: 14,
    color: "#111",
  },
  btnDanger: {
    background: "#ef4444",
    color: "#fff",
    borderColor: "#dc2626",
  },
  btnLeft: {
    background: "#e5e7eb",
    color: "#9ca3af",
    borderColor: "#d1d5db",
    cursor: "not-allowed",
    opacity: 0.85,
  },
  roster: {
    width: "100%",
    height: 100,
    resize: "vertical",
    fontFamily: "ui-monospace, monospace",
    boxSizing: "border-box",
  },
  videoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  video: {
    width: "100%",
    maxWidth: 480,
    background: "#000",
    borderRadius: 8,
  },
  stats: {
    whiteSpace: "pre",
    background: "#eef2ff",
    border: "1px solid #e0e7ff",
    borderRadius: 8,
    padding: 10,
    fontFamily: "ui-monospace, monospace",
    fontSize: 13,
    lineHeight: 1.6,
  },
  log: {
    background: "#111",
    color: "#ddd",
    padding: 10,
    borderRadius: 8,
    height: 360,
    overflow: "auto",
    whiteSpace: "pre-wrap",
    fontFamily: "ui-monospace, monospace",
    fontSize: 12,
  },
};
